import React from 'react';
import { useSearchParams } from 'react-router-dom';

import CloudpassService from '../services/cloudpassService';
import AffiliatesService from '../services/affiliatesService';

interface AuthContextType {
  affServiceInstance: AffiliatesService | undefined;
  cpassServiceInstance: CloudpassService | undefined;
  configState: IConfigParams | undefined;
  init: (cp: string, idp: string, oneTimeToken?: any, sessionCode?: any, countryCode?: any, cpfiltered?: string | null) => void;
}
interface IConfigParams {
  countryCode: string | null;
  cp: string | null;
  deviceToken: string | null;
  sessionCode: string | null;
  idp: string | null;
  isLoading: boolean;
  cpfiltered: string | null;
}

const AuthContext = React.createContext<AuthContextType>(
  {} as AuthContextType
);

/**
 * Get Toolbox auth context
 * @returns auth Context
 */
export function useAuthGuard(): AuthContextType {
  const ctx = React.useContext(AuthContext);

  if (!ctx) {
    throw new Error('You need wrap your app with AuthGuardProvider HOC');
  }

  return ctx;
}

export function AuthGuardProvider({ children }: { children: React.ReactNode }) {
  const [configState, setConfigState] = React.useState<IConfigParams>({
    countryCode: null,
    cp: null,
    deviceToken: null,
    sessionCode: null,
    idp: null,
    isLoading: true,
    cpfiltered: null
  });
  const CLOUDPASS_API_REF = React.useRef<CloudpassService>();
  const AFFILIATES_API_REF = React.useRef<AffiliatesService>();

  AFFILIATES_API_REF.current = new AffiliatesService();

  const init = React.useCallback(async (
    cp: string, 
    idp: string, 
    toolboxUserToken?: string, 
    sessionCode?: string,
    countryCode?: string, 
    cpfiltered?: string | null
  ) => {
    CLOUDPASS_API_REF.current = new CloudpassService(cp);
    setConfigState({
      ...configState,
      countryCode: countryCode || configState.countryCode || null,
      cp,
      deviceToken: toolboxUserToken || null,
      sessionCode: sessionCode || null,
      idp,
      isLoading: false,
      cpfiltered: cpfiltered || null
    });
  },
    [configState]);

  const context = React.useMemo(
    () => ({
      affServiceInstance: AFFILIATES_API_REF.current,
      cpassServiceInstance: CLOUDPASS_API_REF.current,
      configState,
      init
    }),
    [
      AFFILIATES_API_REF,
      CLOUDPASS_API_REF,
      configState,
      init
    ]
  );


  return (
    <AuthContext.Provider value={context}>
      {children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  const authGuard = useAuthGuard();
  const [searchParams] = useSearchParams();

  if (searchParams.size < 2) {
    throw Error('Missing required parameters!');
  }

  const cp = searchParams.get('cp');
  const idp = searchParams.get('idp');
  const deviceToken = searchParams.get('toolbox_user_token');
  const countryCode = searchParams.get('country');
  const cpfiltered = searchParams.get('cpfiltered');
  const sessionCode = searchParams.get('session_code');

  if (!cp || !idp) {
    throw Error(`Missing required parameters!`);
  }

  React.useEffect(() => {
    authGuard.init(cp, idp, deviceToken, sessionCode, countryCode, cpfiltered);
  }, []);

  if (authGuard.configState?.isLoading) {
    return;
  }

  return children;
}
